import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';

import { CancellationReasonEnum } from '@mopla/data-models';
import {
	BrowserLink,
	Checkbox,
	EMlpIconTypes,
	EMplVariantTypes,
	MplButton,
	MplIconMessage,
	MplRadioButton,
	MplTextArea,
} from '@mopla/ui';

import { useDTicketSubscriptionCancelForm } from './hooks';
import {
	CANCEL_REASONS_ORDERED,
	ECancellationCase,
	ICancellationDates,
} from './types';

const MAX_OTHER_REASON_INPUT = 300;

const AGB_URL = 'https://www.mopla.solutions/de/agb';
const TARIF_URL =
	'https://www.mopla.solutions/de/tarifbestimmung-und-beforderungsbedingungen';

interface IDTicketSubscriptionCancelFormProps {
	onSubmit: (reason: CancellationReasonEnum, otherReasonText?: string) => void;
	isLoading: boolean;
	dates: ICancellationDates;
	cancellationCase?: ECancellationCase;
}

export const DTicketSubscriptionCancelForm = ({
	onSubmit,
	isLoading,
	dates,
	cancellationCase,
}: IDTicketSubscriptionCancelFormProps) => {
	const { t } = useTranslation('dticketSubscriptionCancel');
	const {
		control,
		isOtherReasonSelected,
		isFormValid,
		onSubmit: handleSubmit,
	} = useDTicketSubscriptionCancelForm();

	const linkComponents = {
		agbLink: <BrowserLink url={AGB_URL} />,
		tarifLink: <BrowserLink url={TARIF_URL} />,
	};

	// Hide the cancellation warning for preorder cancellations made more than
	// 24h before subscription start — no costs/loss of access applies yet.
	const showWarning =
		cancellationCase !== ECancellationCase.PreorderCancelInTime;

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Stack gap={4}>
				<Controller
					name="reason"
					control={control}
					render={({ field }) => (
						<Stack gap={2}>
							{CANCEL_REASONS_ORDERED.map((reason) => (
								<MplRadioButton
									key={reason}
									text={t(`reasons.${reason}`)}
									checked={field.value === reason}
									onChange={() => field.onChange(reason)}
									multiline
									data-testid={`dticket-cancel-reason-${reason}`}
								/>
							))}

							{isOtherReasonSelected && (
								<Controller
									name="otherReasonText"
									control={control}
									render={({ field }) => (
										<MplTextArea
											minRows={3}
											maxRows={6}
											label={t('other_reason_label')}
											inputProps={{ maxLength: MAX_OTHER_REASON_INPUT }}
											helperText={`${
												field.value?.length || 0
											}/${MAX_OTHER_REASON_INPUT}`}
											data-testid="dticket-cancel-other-reason-input"
											{...field}
										/>
									)}
								/>
							)}
						</Stack>
					)}
				/>

				{showWarning && (
					<MplIconMessage
						type={EMlpIconTypes.Warning}
						variant={EMplVariantTypes.Warning}
						message={
							<Trans
								i18nKey="dticketSubscriptionCancel:warning_message"
								values={dates}
								components={linkComponents}
							/>
						}
						dataTestid="dticket-cancelForm-warning-iconMessage"
					/>
				)}

				<Controller
					name="acceptConditions"
					control={control}
					render={({ field }) => (
						<Checkbox
							label={
								<Trans
									i18nKey="dticketSubscriptionCancel:accept_conditions"
									components={linkComponents}
								/>
							}
							checkboxTestId="dticket-cancel-accept-conditions-checkbox"
							{...field}
						/>
					)}
				/>

				<MplButton
					type="submit"
					fullWidth
					variant="contained"
					color="primary"
					disabled={!isFormValid || isLoading}
					showLoader={isLoading}
					testId="dticket-cancelForm-submit-button"
				>
					{t('cancel_button')}
				</MplButton>
			</Stack>
		</form>
	);
};
